import React, { createContext, useContext, useState } from 'react';
import { Gender, Hero, SuccessfulResult, getConfigs } from '../configs';
import * as Sentry from '@sentry/react';
import { ENABLED_COUNTRY_CODES, JOIN_US_URL_BY_COUNTRY, SHARE_BASE_URL } from '../constants';
import { getHeroFromResults, isDeviceMobile, sendEvent } from '../utils/utils';

type Screen =
  | 'home'
  | 'prologue'
  | 'story'
  | 'question'
  | 'building'
  | 'buildingComplete'
  | 'result';

export type Flow = 'stg' | 'normal';

export interface GlobalState {
  flow: Flow | null;
  setFlow: (flow: Flow) => void;
  shareModalUrl: string | null;
  openShareCard: (origin: string) => void;
  closeShareCard: () => void;
  currentScreen: Screen;
  setCurrentScreen: (currentScreen: Screen) => void;
  results: Array<SuccessfulResult>;
  setResults: (results: Array<SuccessfulResult>) => void;
  selectedGender: Gender | null;
  setSelectedGender: (selectedGender: Gender | null) => void;
  whoIsStgModalOpen: boolean;
  setWhoIsStgModalOpen: (whoIsStgModalOpen: boolean) => void;
  missionFailed: boolean;
  setMissionFailed: (missionFailed: boolean) => void;
  notificationVisible: boolean;
  setNotificationVisible: (notificationVisible: boolean) => void;
  notificationOpen: boolean;
  setNotificationOpen: (notificationOpen: boolean) => void;
  notificationAlreadyOpened: boolean;
  setNotificationAlreadyOpened: (notificationAlreadyOpened: boolean) => void;
  joinUsFooterModalOpen: boolean;
  setJoinUsFooterModalOpen: (joinUsFooterModalOpen: boolean) => void;
  resetState(): void;
  joinUsUrl: string;
  setCountryCode: (countryCode: string) => void;
}

// We assume this GlobalStateContext will never be used unless inside
// the GlobalStateContext.Provider, and so the default is never used.
export const GlobalStateContext = createContext<GlobalState>({} as GlobalState);

export const useGlobalState = () => useContext(GlobalStateContext);

interface StateProviderProps {
  children: React.ReactNode;
}

export function GlobalStateProvider({ children }: StateProviderProps) {
  const [flow, setFlow] = useState<Flow | null>(null);
  const [shareModalUrl, setShareModalUrl] = useState<string | null>(null);
  const [currentScreen, setCurrentScreen] = useState<Screen>('home');
  const [results, setResults] = useState<Array<SuccessfulResult>>([]);
  const [selectedGender, setSelectedGender] = useState<Gender | null>(null);
  const [whoIsStgModalOpen, setWhoIsStgModalOpen] = useState(false);
  const [missionFailed, setMissionFailed] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationAlreadyOpened, setNotificationAlreadyOpened] = useState(false);
  const [joinUsFooterModalOpen, setJoinUsFooterModalOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(ENABLED_COUNTRY_CODES[0]);
  // When adding state here, also add to resetState

  const configs = getConfigs(flow);

  function openShareCard(origin: string) {
    const shareUrl =
      (currentScreen === 'result'
        ? SHARE_BASE_URL + '/' + getHeroFromResults(results) + '-' + selectedGender + '/'
        : SHARE_BASE_URL) +
      '?country=' +
      countryCode.toUpperCase();
    if (window.navigator.share && isDeviceMobile()) {
      sendEvent(
        flow,
        'OpenNativeShare',
        {
          from: origin,
          screen: currentScreen,
          hero:
            currentScreen === 'result' ? getHeroFromResults(results) + '-' + selectedGender : null,
        },
        true,
      );
      window.navigator
        .share({
          title: configs.gameName,
          text:
            configs.gameName +
            '\n' +
            configs.share.shareTitle +
            '\n' +
            configs.share.shareDescription,
          url: shareUrl + '&source=ns',
        })
        .catch(error => {
          if (
            error &&
            (error.toString().includes('AbortError') ||
              error.toString().includes('Share canceled') ||
              error.toString().includes('cancellation of share'))
          ) {
            sendEvent(flow, 'CancelNativeShare', {
              from: origin,
              screen: currentScreen,
              hero:
                currentScreen === 'result'
                  ? getHeroFromResults(results) + '-' + selectedGender
                  : null,
            });
            return;
          }
          setShareModalUrl(shareUrl);
          console.error(error);
          Sentry.captureException(error);
        });
    } else {
      sendEvent(flow, 'OpenShareCard', {
        from: origin,
        screen: currentScreen,
        hero:
          currentScreen === 'result' ? getHeroFromResults(results) + '-' + selectedGender : null,
      });
      setShareModalUrl(shareUrl);
    }
  }

  function closeShareCard() {
    setShareModalUrl(null);
  }

  const resetState = () => {
    setShareModalUrl(null);
    setCurrentScreen('home');
    setResults([]);
    setSelectedGender(null);
    setMissionFailed(false);
    setNotificationVisible(false);
  };

  const globalState: GlobalState = {
    flow,
    setFlow,
    shareModalUrl,
    openShareCard,
    closeShareCard,
    currentScreen,
    setCurrentScreen,
    results,
    setResults: setResults,
    selectedGender,
    setSelectedGender,
    whoIsStgModalOpen,
    setWhoIsStgModalOpen,
    missionFailed,
    setMissionFailed,
    notificationVisible,
    setNotificationVisible,
    notificationOpen,
    setNotificationOpen,
    notificationAlreadyOpened,
    setNotificationAlreadyOpened,
    joinUsFooterModalOpen,
    setJoinUsFooterModalOpen,
    resetState,
    get joinUsUrl() {
      return JOIN_US_URL_BY_COUNTRY[countryCode];
    },
    setCountryCode,
  };

  return <GlobalStateContext.Provider value={globalState}>{children}</GlobalStateContext.Provider>;
}
